import { useCallback, useEffect, useRef, useState } from "react";
import Pusher from "pusher-js";
import { PUSHER_CLUSTER, PUSHER_KEY } from "../config/env";
import { isLivePhase, phaseOf } from "../lib/trip";
import { fetchSharedTrip } from "../services/trip";
import type { DriverPosition, TripData } from "../types/trip";

export type LoadState = "loading" | "ready" | "notFound" | "error";
export type Connection = "live" | "reconnecting" | "offline";

const POLL_MS = 20_000;

interface LiveTrip {
  state: LoadState;
  trip: TripData | null;
  driver: DriverPosition | null;
  etaMinutes: number | null;
  lastUpdate: number | null;
  connection: Connection;
  retry: () => void;
}

function positionFrom(value: unknown): DriverPosition | null {
  if (!value || typeof value !== "object") return null;
  const loc = value as { latitude?: unknown; longitude?: unknown; heading?: unknown };
  if (typeof loc.latitude !== "number" || typeof loc.longitude !== "number") return null;
  return {
    lat: loc.latitude,
    lng: loc.longitude,
    heading: typeof loc.heading === "number" ? loc.heading : null,
  };
}

/**
 * Keeps a shared trip current: initial fetch, the trip's Pusher channel for
 * live position/status, a poll while the trip is live (Pusher can silently
 * drop on mobile browsers), and a refresh whenever the tab comes back into
 * view or the network returns.
 */
export function useLiveTrip(token: string | undefined): LiveTrip {
  const [state, setState] = useState<LoadState>("loading");
  const [trip, setTrip] = useState<TripData | null>(null);
  const [driver, setDriver] = useState<DriverPosition | null>(null);
  const [etaMinutes, setEtaMinutes] = useState<number | null>(null);
  const [lastUpdate, setLastUpdate] = useState<number | null>(null);
  const [connection, setConnection] = useState<Connection>("live");
  const statusRef = useRef<string | undefined>(undefined);
  const inFlight = useRef(false);

  const refresh = useCallback(async () => {
    if (!token || inFlight.current) return;
    inFlight.current = true;
    try {
      const result = await fetchSharedTrip(token);
      if (result.kind === "ok") {
        const next = result.trip;
        if (next.tripStatus !== statusRef.current) setEtaMinutes(null);
        statusRef.current = next.tripStatus;
        setTrip(next);
        const seeded = positionFrom(next.lastDriverLocation);
        if (seeded) setDriver((current) => current ?? seeded);
        setLastUpdate(Date.now());
        setState("ready");
      } else if (result.kind === "notFound") {
        setState((current) => (current === "ready" ? current : "notFound"));
      } else {
        setState((current) => (current === "ready" ? current : "error"));
        if (!navigator.onLine) setConnection("offline");
      }
    } finally {
      inFlight.current = false;
    }
  }, [token]);

  useEffect(() => {
    setState("loading");
    setTrip(null);
    setDriver(null);
    statusRef.current = undefined;
    void refresh();
  }, [refresh]);

  const tripId = trip?._id;
  const live = trip ? isLivePhase(phaseOf(trip.tripStatus)) : false;

  useEffect(() => {
    if (!tripId || !live || !PUSHER_KEY) return;
    const pusher = new Pusher(PUSHER_KEY, { cluster: PUSHER_CLUSTER });
    const channelName = `trip-${tripId}`;
    const channel = pusher.subscribe(channelName);
    let wasDisconnected = false;

    pusher.connection.bind(
      "state_change",
      ({ current }: { current: string }) => {
        if (current === "connected") {
          setConnection("live");
          if (wasDisconnected) void refresh();
          wasDisconnected = false;
        } else if (current === "connecting" || current === "unavailable") {
          setConnection("reconnecting");
          wasDisconnected = true;
        } else if (current === "failed" || current === "disconnected") {
          setConnection("offline");
          wasDisconnected = true;
        }
      }
    );

    channel.bind_global((event: string, data: Record<string, unknown>) => {
      if (event.startsWith("pusher:") || !data || typeof data !== "object") return;
      const position = positionFrom(data.location) ?? positionFrom(data);
      if (position) setDriver(position);
      const incoming = data.trip as TripData | undefined;
      const incomingStatus = incoming?.tripStatus;
      if (incomingStatus && incomingStatus !== statusRef.current) {
        void refresh();
        return;
      }
      if (typeof data.duration === "number" && data.duration > 0) {
        setEtaMinutes(Math.round(data.duration));
      }
      if (event === "driverCancelled" || event === "cancelled" || event === "completed") {
        void refresh();
      }
      setLastUpdate(Date.now());
    });

    return () => {
      pusher.unsubscribe(channelName);
      pusher.disconnect();
    };
  }, [tripId, live, refresh]);

  useEffect(() => {
    if (!token || !live) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh();
    }, POLL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    const onOnline = () => {
      setConnection("reconnecting");
      void refresh();
    };
    const onOffline = () => setConnection("offline");
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, [token, live, refresh]);

  return {
    state,
    trip,
    driver,
    etaMinutes,
    lastUpdate,
    connection,
    retry: () => {
      setState("loading");
      void refresh();
    },
  };
}
